/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import { cloneDeep } from 'lodash';
import Types from '../constants/actionTypes';
import * as API from '../utils/api';
import Store from '../store/store';
import { uploadAttachments } from './attachmentActions';
import { applicationError } from './notificationActions';
import { fetchAllTaskTypes } from './taskTypeAction';
import { updateSingleResult, addSingleResult } from './commonDispatches';

const dataFormatter = new Jsona();
const currentCadenceId = () => Store.getState().cadenceReducer.cadence?.id;

export function updateTaskList(taskList) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return dispatch({ type: Types.UPDATE_TASK_LIST, tasks: taskList });
  };
}

export function updateTaskFromBroadcast(task) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: task.task_type });
    dispatch({ type: Types.RECEIVE_TASK, task });
    updateSingleResult(dispatch, task.work_package || {});
  };
}

export function addTaskFromBroadcast(task) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    dispatch({ type: Types.RECEIVE_TASK, task });
    dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: task.task_type });
    updateSingleResult(dispatch, task.work_package || {});
  };
}

export function removeTaskFromBroadcast(taskId) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    dispatch({ type: Types.DELETE_TASK_WITH_ID, taskId });
  };
}

export function removeFromTaskList(task) {
  return (dispatch) => dispatch({ type: Types.DELETE_TASK, task });
}

export function fetchTask(task) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TASK });
    return API.get(`tasks/${task.id}`)
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        dispatch({ type: Types.RECEIVE_TASK, task });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function fetchCadenceTasks(cadenceId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TASK });
    return API.get(`cadences/${cadenceId}/tasks`)
      .then((response) => {
        const tasks = response ? dataFormatter.deserialize(response) : [];
        dispatch({ type: Types.RECEIVE_ALL_TASKS, tasks });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function fetchAllTasks() {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TASK });
    return API.get('tasks')
      .then((response) => {
        const tasks = dataFormatter.deserialize(response);
        dispatch({ type: Types.RECEIVE_ALL_TASKS, tasks });
      })
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_TASK_FAILED });
      });
  };
}

export function createTask(data, attachments, close = true) {
  const dataCopy = cloneDeep(data);
  if (dataCopy.task_type) delete dataCopy.task_type;
  const { task_owner_ids: ownerIds, task_type: taskType } = data;
  const { modalReducer } = Store.getState();
  const { openModals } = modalReducer;

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.post('tasks', {
      owner_ids: ownerIds,
      task_type: taskType,
      task: dataCopy
    })
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        const newTaskType = task.task_type || {};
        const workPackage = task.work_package || {};
        return dispatch(uploadAttachments(attachments, task.id))
          .then(() => {
            if (close && openModals && openModals.indexOf('newTask') !== -1) {
              dispatch({ type: Types.CLOSE_MODAL, id: 'newTask' });
            }
            dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: newTaskType });
            dispatch({ type: Types.RECEIVE_TASK, task });

            updateSingleResult(dispatch, workPackage);
            dispatch({ type: Types.END_UPLOAD_ATTACHMENT });
          })
          .catch(() => {
            dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: newTaskType });
            dispatch({ type: Types.RECEIVE_TASK, task });
            updateSingleResult(dispatch, workPackage);
            dispatch({ type: Types.END_UPLOAD_ATTACHMENT });
            dispatch(
              applicationError('It was not possible to upload all the files')
            );
          });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateTask(taskId, data) {
  const dataCopy = cloneDeep(data);
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}`, dataCopy)
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        const workPackage = task.work_package || {};
        dispatch({ type: Types.RECEIVE_TASK, task });
        dispatch(fetchAllTaskTypes());
        updateSingleResult(dispatch, workPackage);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function cloneTask(task, parentId, ownerIds) {
  const data = {
    task,
    cadence_id: currentCadenceId(),
    work_package_id: parentId,
    owner_ids: ownerIds
  };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${task.id}/clone`, data)
      .then((response) => response.json())
      .then((response) => {
        const responseTask = response
          ? dataFormatter.deserialize(response)
          : {};
        const newTaskType = responseTask.task_type || {};
        const workPackage = responseTask.work_package || {};
        dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: newTaskType });
        dispatch({ type: Types.RECEIVE_TASK, task: responseTask });
        updateSingleResult(dispatch, workPackage);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function moveTask(task, parentId) {
  const data = {
    task,
    cadence_id: currentCadenceId(),
    work_package_id: parentId,
    move: true
  };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${task.id}/clone`, data)
      .then((response) => response.json())
      .then((response) => {
        dispatch({ type: Types.DELETE_TASK, task });

        const responseTask = response
          ? dataFormatter.deserialize(response)
          : {};
        const newTaskType = responseTask.task_type || {};
        const workPackage = responseTask.work_package || {};
        dispatch({ type: Types.SEND_TASK });
        dispatch({ type: Types.RECEIVE_TASK_TYPE, taskType: newTaskType });
        dispatch({ type: Types.RECEIVE_TASK, task: responseTask });
        updateSingleResult(dispatch, workPackage);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateTaskRank(taskId, data) {
  const dataCopy = data;
  dataCopy.cadence_id = currentCadenceId();

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/rank`, dataCopy)
      .then((response) => response.json())
      .then((response) => {
        const tasks = dataFormatter.deserialize(response);
        dispatch({ type: Types.RECEIVE_WORK_PACKAGE_TASKS, tasks });
      })
      .catch((error) => {
        dispatch({ type: Types.CANCEL_SEND_TASK });
        dispatch(applicationError(error));
      });
  };
}

export function addTaskOwner(taskId, ownerId) {
  const payload = { owner_id: ownerId, cadence_id: currentCadenceId() };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/add-owner`, payload)
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        dispatch({ type: Types.RECEIVE_TASK, task });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function editTaskOwner(taskId, ownerId, newOwnerId) {
  const payload = {
    owner_id: ownerId,
    new_owner_id: newOwnerId,
    cadence_id: currentCadenceId()
  };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/edit-owner`, payload)
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        dispatch({ type: Types.RECEIVE_TASK, task });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeTaskOwner(taskId, ownerId) {
  const payload = { owner_id: ownerId, cadence_id: currentCadenceId() };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/remove-owner`, payload)
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        dispatch({ type: Types.RECEIVE_TASK, task });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function blockTask(taskId) {
  const payload = { cadence_id: currentCadenceId() };

  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/block`, payload).catch((error) =>
      dispatch(applicationError(error))
    );
  };
}

export function deleteTask(task) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    if (task.deleted_at) dispatch(removeFromTaskList(task));
    return API.del(`tasks/${task.id}`)
      .then((response) => response.json())
      .then((response) => {
        const taskDeleted = response ? dataFormatter.deserialize(response) : {};
        const workPackage = taskDeleted.work_package || {};
        if (!task.deleted_at) {
          dispatch({ type: Types.RECEIVE_TASK, task: taskDeleted });
        }
        updateSingleResult(dispatch, workPackage);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function restoreTask(taskId) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.put(`tasks/${taskId}/restore`)
      .then((response) => response.json())
      .then((response) => {
        const task = response ? dataFormatter.deserialize(response) : {};
        dispatch({ type: Types.RECEIVE_TASK, task });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function changeTaskToStory(task) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_TASK });
    return API.post(`tasks/${task.id}/change-to-story`)
      .then((response) => response.json())
      .then((response) => {
        const workPackage = response.work_package
          ? dataFormatter.deserialize(response.work_package)
          : {};

        addSingleResult(dispatch, false, workPackage, response.ranks_and_ids);
        dispatch({
          type: Types.DELETE_TASK,
          task
        });
        dispatch({ type: Types.CLOSE_MODAL, id: 'taskDetail' });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}
